import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type StockTypeCategory = "additive" | "packaging";

export interface StockType {
  id: string;
  name: string;
  code: string;
  category: StockTypeCategory;
  is_active: boolean;
  org_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateStockTypeInput {
  name: string;
  code: string;
  category: StockTypeCategory;
  is_active?: boolean;
}

export interface UpdateStockTypeInput {
  name?: string;
  code?: string;
  category?: StockTypeCategory;
  is_active?: boolean;
}

/**
 * Hook to fetch all stock types for the current organization
 * @param activeOnly - If true, only returns active stock types (default: true)
 * @param category - Optional category filter ('additive' or 'packaging')
 * @param searchTerm - Optional search term to filter by name or code
 */
export function useStockTypes(
  activeOnly: boolean = true,
  category?: StockTypeCategory,
  searchTerm?: string
) {
  return useQuery({
    queryKey: ["stock-types", activeOnly, category, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("stock_type")
        .select("*")
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      if (category) {
        query = query.eq("category", category);
      }

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,code.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as StockType[];
    },
  });
}

/**
 * Hook to fetch a single stock type by ID
 */
export function useStockType(id: string | undefined) {
  return useQuery({
    queryKey: ["stock-type", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("stock_type")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as StockType;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new stock type
 */
export function useCreateStockType() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateStockTypeInput) => {
      // Get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("stock_type")
        .insert([{
          name: input.name,
          code: input.code.toUpperCase(),
          category: input.category,
          is_active: input.is_active ?? true,
          org_id: userData.org_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as StockType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stock-types"] });
      toast({
        title: "Stock type created",
        description: "The stock type has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating stock type",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to update an existing stock type
 */
export function useUpdateStockType() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateStockTypeInput }) => {
      const updateData: UpdateStockTypeInput = { ...input };
      if (updateData.code) {
        updateData.code = updateData.code.toUpperCase();
      }

      const { data, error } = await supabase
        .from("stock_type")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as StockType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stock-types"] });
      queryClient.invalidateQueries({ queryKey: ["stock-type"] });
      toast({
        title: "Stock type updated",
        description: "The stock type has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating stock type",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to delete a stock type (soft delete by deactivating)
 */
export function useDeleteStockType() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from("stock_type")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stock-types"] });
      toast({
        title: "Stock type deactivated",
        description: "The stock type has been deactivated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deactivating stock type",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to get stock types formatted for Select/Dropdown components
 */
export function useStockTypeOptions(category?: StockTypeCategory) {
  const { data: stockTypes, isLoading, error } = useStockTypes(true, category);

  const options = stockTypes?.map((st) => ({
    value: st.id,
    label: `${st.name} (${st.code})`,
    name: st.name,
    code: st.code,
    category: st.category,
  })) ?? [];

  return { options, isLoading, error };
}
